import axios from "axios";

const SKEW_MS = 30_000;

interface TokenSet {
  tokenType: string;
  accessToken: string;
  refreshToken: string;
  expiredAt: string;
}

let inflight: Promise<void> | null = null;

const tokenAxios = axios.create({
  baseURL: process.env.REACT_APP_API_VALLARIS,
  timeout: 10_000,
  headers: { "Content-Type": "application/x-www-form-urlencoded" },
});

const parseExpiry = (raw: string | null): number => {
  if (!raw) return 0;
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : 0;
};

export const isAccessTokenExpired = (): boolean => {
  const expiredAt = parseExpiry(localStorage.getItem("expired_at"));
  return Date.now() + SKEW_MS >= expiredAt;
};

const writeTokens = (data: TokenSet): void => {
  localStorage.setItem("token_type", data.tokenType);
  localStorage.setItem("access_token", data.accessToken);
  localStorage.setItem("refresh_token", data.refreshToken);
  localStorage.setItem("expired_at", data.expiredAt);
};

export const clearAuth = (): void => {
  localStorage.removeItem("token_type");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("expired_at");
  localStorage.removeItem("extension");
  localStorage.removeItem("user");
  localStorage.removeItem("uid");
};

export const refreshAccessToken = (force = false): Promise<void> => {
  if (inflight) return inflight;

  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) return Promise.reject(new Error("NO_REFRESH_TOKEN"));

  // If the caller is not forcing (e.g., proactive check from getAuthHeader)
  // and the local clock says we're still fresh, skip the network round-trip.
  if (!force && !isAccessTokenExpired()) return Promise.resolve();

  inflight = (async () => {
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: refreshToken,
    }).toString();

    const { data } = await tokenAxios.post<TokenSet>(
      "/core/api/managements/1.0/token",
      body
    );

    if (!data || !data.accessToken || !data.refreshToken) {
      throw new Error("INVALID_TOKEN_RESPONSE");
    }

    writeTokens(data);
  })().finally(() => {
    inflight = null;
  });

  return inflight;
};

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === "access_token" && e.newValue && inflight) {
      inflight = null;
    }
  });
}

export const getAuthHeader = async (): Promise<{ Authorization: string }> => {
  const accessToken = localStorage.getItem("access_token");
  if (!accessToken) throw new Error("UNAUTHENTICATED");

  if (isAccessTokenExpired()) {
    await refreshAccessToken();
  }

  const tokenType = localStorage.getItem("token_type") ?? "Bearer";
  const fresh = localStorage.getItem("access_token");
  if (!fresh) throw new Error("UNAUTHENTICATED");
  return { Authorization: `${tokenType} ${fresh}` };
};

export const notifyAuthExpired = (): void => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("auth:expired"));
  }
};

export const __resetForTests = (): void => {
  inflight = null;
};
